import classNames from 'classnames';
import { CSSProperties, ReactNode, useMemo } from 'react';
import { IDivProps } from '../../types/props/DivProps';
import './Grid.scss';
import ClassName from '../../types/ClassName';
import GridItem from '../GridItem/GridItem';

type FlexValue = 'start' | 'end' | 'center' | 'stretch';
type ContentFlexValue = FlexValue | 'space-around' | 'space-between' | 'space-evenly';

export interface IGridProps {
  children: ReactNode;
  gridTemplateColumns?: GridTemplateMeasurement[];
  gridTemplateRows?: GridTemplateMeasurement[];
  columnGap?: number;
  rowGap?: number;
  justifyItems?: FlexValue;
  alignItems?: FlexValue;
  justifyContent?: ContentFlexValue;
  alignContent?: ContentFlexValue;
  gridAutoColumns?: GridTemplateMeasurement[];
  gridAutoRows?: GridTemplateMeasurement[];
  gridAutoFlow?: 'row' | 'column' | 'row dense' | 'column dense';
  style?: Omit<
    IDivProps,
    | 'gridTemplateColumns'
    | 'gridTemplateRows'
    | 'columnGap'
    | 'rowGap'
    | 'alignItems'
    | 'justifyItems'
    | 'justifyContent'
    | 'alignContent'
    | 'gridAutoColumns'
    | 'gridAutoRows'
    | 'gridAutoFlow'
  >;
  className?: ClassName;
}

export type GridTemplateMeasurement =
  | { type: 'pixel'; value: number }
  | { type: 'percentage'; value: number }
  | { type: 'fraction'; value: number }
  | { type: 'auto' };

function getValue(measurement: GridTemplateMeasurement) {
  switch (measurement.type) {
    case 'pixel':
      return `${measurement.value}px`;
    case 'auto':
      return 'auto';
    case 'fraction':
      return `${measurement.value}fr`;
    case 'percentage':
      return `${measurement.value}%`;
  }
}

function flattenMeasurements(measurements: GridTemplateMeasurement[]) {
  return measurements.map(getValue).join(' ');
}

function getMeasurementValue(
  measurements: GridTemplateMeasurement[] | null | undefined
): CSSProperties['gridColumnStart'] {
  return measurements ? flattenMeasurements(measurements) : undefined;
}

function Grid({
  gridTemplateColumns,
  gridTemplateRows,
  columnGap,
  rowGap,
  alignItems,
  justifyItems,
  justifyContent,
  alignContent,
  gridAutoColumns,
  gridAutoRows,
  gridAutoFlow,
  children,
  style,
  className,
}: IGridProps) {
  const classes = classNames(
    'grid',
    {
      [`justify-items-${justifyItems}`]: justifyItems !== undefined,
      [`align-items-${alignItems}`]: alignItems !== undefined,
      [`justify-content-${justifyContent}`]: justifyContent !== undefined,
      [`align-content-${alignContent}`]: alignContent !== undefined,
    },
    className
  );

  const styles: CSSProperties = useMemo(
    () => ({
      ...style,
      rowGap,
      columnGap,
      gridTemplateColumns: getMeasurementValue(gridTemplateColumns),
      gridTemplateRows: getMeasurementValue(gridTemplateRows),
      gridAutoColumns: getMeasurementValue(gridAutoColumns),
      gridAutoRows: getMeasurementValue(gridAutoRows),
      gridAutoFlow,
    }),
    [columnGap, gridAutoColumns, gridAutoFlow, gridAutoRows, gridTemplateColumns, gridTemplateRows, rowGap, style]
  );

  return (
    <div className={classes} style={styles}>
      {children}
    </div>
  );
}

Grid.Item = GridItem;

export default Grid;
